import { useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Database,
  Archive,
  Briefcase,
  Users,
  Loader2,
  DollarSign,
} from 'lucide-react'
import {
  useGetDatascopeQuery,
  useUpdateDatascopeMutation,
} from '@/redux/service/datascope'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import { Skeleton } from '@/components/ui/skeleton'

interface SettingsData {
  projectHourlyRate: number
  employeeHourlyRate: number
  existingExpenseRatio: number
  datascope: boolean
  archived: boolean
}

const INITIAL_SETTINGS: SettingsData = {
  projectHourlyRate: 0,
  employeeHourlyRate: 0,
  existingExpenseRatio: 0,
  datascope: false,
  archived: false,
}

const DataCalculation = () => {
  const { data: datascopeData, isLoading: isDatascopeLoading } =
    useGetDatascopeQuery()

  const [updateDatascope, { isLoading: isUpdatingDatascope }] =
    useUpdateDatascopeMutation()

  const initialValues = useMemo(() => {
    if (datascopeData?.data?.[0]) {
      const apiData = datascopeData.data[0]
      return {
        projectHourlyRate: Number(apiData.project_hourly_rate) || 0,
        employeeHourlyRate: Number(apiData.employee_hourly_rate) || 0,
        existingExpenseRatio:
          Number(datascopeData?.data?.[0]?.expense_ratio) * 100 || 0,
        datascope: Boolean(apiData.datascope),
        archived: Boolean(apiData.archived),
      }
    }
    return INITIAL_SETTINGS
  }, [datascopeData])

  const formik = useFormik<SettingsData>({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      projectHourlyRate: Yup.number()
        .required('Required')
        .min(0, 'Must be positive'),
      employeeHourlyRate: Yup.number()
        .required('Required')
        .min(0, 'Must be positive'),
      existingExpenseRatio: Yup.number()
        .required('Required')
        .min(0, 'Must be positive'),
    }),
    onSubmit: async values => {
      try {
        await updateDatascope({
          project_hourly_rate: values.projectHourlyRate,
          employee_hourly_rate: values.employeeHourlyRate,
          datascope: values.datascope,
          expense_ratio: values.existingExpenseRatio / 100,
          archived: values.archived,
        }).unwrap()
        toast.success('Settings saved')
      } catch (error) {
        const message =
          (error as { data?: { message?: string } })?.data?.message ||
          'Update failed'
        toast.error(message)
      }
    },
  })

  if (isDatascopeLoading) {
    return (
      <div className='space-y-8 animate-pulse'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-12'>
          <div className='space-y-3'>
            <Skeleton className='h-8 w-40' />
            <Skeleton className='h-4 w-full' />
          </div>
          <div className='md:col-span-2 space-y-4'>
            {[1, 2].map(i => (
              <Skeleton key={i} className='h-24 w-full rounded-2xl' />
            ))}
            <div className='grid grid-cols-2 gap-4'>
              <Skeleton className='h-32 rounded-2xl' />
              <Skeleton className='h-32 rounded-2xl' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='h-full flex flex-col animate-in fade-in slide-in-from-bottom-1 duration-700 overflow-hidden'>
      <div className='flex-1 overflow-auto custom-scrollbar pr-2'>
        <form onSubmit={formik.handleSubmit} className='space-y-12'>
          <div className='flex flex-col md:flex-row justify-between gap-12'>
            {/* Header Section */}
            <div className='space-y-3 w-full md:w-[30%] shrink-0'>
              <h3 className='text-xl font-medium tracking-tight text-foreground'>
                Sync Configuration
              </h3>
              <p className='text-sm text-muted-foreground leading-relaxed '>
                Define how your ClickUp data is processed and mapped to
                financial baselines.
              </p>
            </div>

            <div className='md:col-span-2 space-y-6 w-[60%]'>
              {/* Minimal Toggles */}
              <div className='space-y-3'>
                <div className='flex items-start justify-between p-6 rounded-2xl border border-border bg-card/50 hover:bg-card transition-colors duration-200'>
                  <div className='flex gap-4 items-center'>
                    <div className='p-2 rounded-lg bg-muted text-muted-foreground'>
                      <Database className='w-5 h-5' />
                    </div>
                    <div className='space-y-1'>
                      <Label className='text-sm font-semibold'>
                        Include Unmapped Data
                      </Label>
                      <p className='text-xs text-muted-foreground leading-normal max-w-md'>
                        If ON, we import ALL time logs from ClickUp. Projects or
                        employees not configured will use the default rates
                        below. If OFF, only configured projects and employees
                        are included.
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formik.values.datascope}
                    onCheckedChange={c => formik.setFieldValue('datascope', c)}
                    className='scale-90'
                  />
                </div>

                <div className='flex items-start justify-between p-6 rounded-2xl border border-border bg-card/50 hover:bg-card transition-colors duration-200'>
                  <div className='flex gap-4 items-center'>
                    <div className='p-2 rounded-lg bg-muted text-muted-foreground '>
                      <Archive className='w-5 h-5 ' />
                    </div>
                    <div className='space-y-1'>
                      <Label className='text-sm font-semibold'>
                        Show Archived Data
                      </Label>
                      <p className='text-xs text-muted-foreground leading-normal max-w-md'>
                        If enabled, archived employees and projects will also be
                        displayed with their default values.
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formik.values.archived}
                    onCheckedChange={c => formik.setFieldValue('archived', c)}
                    className='scale-90'
                  />
                </div>
              </div>

              {/* Minimal Inputs */}
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div className='p-6 rounded-2xl border border-border space-y-4'>
                  <div className='flex items-center gap-2 text-muted-foreground'>
                    <Briefcase className='w-4 h-4' />
                    <Label className='text-xs font-medium uppercase tracking-wider'>
                      Project Bill Rate
                    </Label>
                  </div>
                  <div className='relative'>
                    <DollarSign className='absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                    <Input
                      name='projectHourlyRate'
                      type='number'
                      value={formik.values.projectHourlyRate}
                      onChange={formik.handleChange}
                      className='border-0 border-b border-transparent focus-visible:ring-0 focus-visible:border-primary rounded-none px-6 text-2xl font-semibold bg-transparent h-auto py-1 shadow-none'
                    />
                  </div>
                </div>

                <div className='p-6 rounded-2xl border border-border space-y-4'>
                  <div className='flex items-center gap-2 text-muted-foreground'>
                    <Users className='w-4 h-4' />
                    <Label className='text-xs font-medium uppercase tracking-wider'>
                      Employee Salary
                    </Label>
                  </div>
                  <div className='relative'>
                    <DollarSign className='absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                    <Input
                      name='employeeHourlyRate'
                      type='number'
                      value={formik.values.employeeHourlyRate}
                      onChange={formik.handleChange}
                      className='border-0 border-b border-transparent focus-visible:ring-0 focus-visible:border-primary rounded-none px-6 text-2xl font-semibold bg-transparent h-auto py-1 shadow-none'
                    />
                  </div>
                </div>
              </div>
              <div className='p-6 rounded-2xl border border-border space-y-4'>
                <div className='flex items-center gap-2 text-muted-foreground'>
                  <Users className='w-4 h-4' />
                  <Label className='text-xs font-medium uppercase tracking-wider'>
                    Default Expense Ratio
                  </Label>
                </div>
                <div className='relative'>
                  <DollarSign className='absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                  <Input
                    name='existingExpenseRatio'
                    type='number'
                    value={formik.values.existingExpenseRatio}
                    onChange={formik.handleChange}
                    className='border-0 border-b border-transparent focus-visible:ring-0 focus-visible:border-primary rounded-none px-6 text-2xl font-semibold bg-transparent h-auto py-1 shadow-none'
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className='flex justify-end py-8 border-t border-border'>
            <Button
              type='submit'
              disabled={isUpdatingDatascope}
              variant='default'
              className='h-11 px-8 rounded-full shadow-lg shadow-primary/20 transition-all active:scale-[0.98]'
            >
              {isUpdatingDatascope ? (
                <Loader2 className='w-4 h-4 animate-spin' />
              ) : (
                <>Save Configuration</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DataCalculation
